import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { studentAPI, accountLinkAPI } from '../services/api';
import feedbackAPI from '../services/feedbackAPI';
import StoryInviteCard from '../components/StoryInviteCard';
import Logo from '../components/Logo';
import Avatar from '../components/Avatar';
import InboxBell from '../components/InboxBell';
import { getMyCard } from '../services/foundingClass';
import './Profile.css';

type Stream = 'natural' | 'social' | '';

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { student, logout, refreshProfile } = useAuth();
  const { theme } = useTheme();
  const dark = theme === 'dark';

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showId, setShowId] = useState(false);
  const [hasFoundingCard, setHasFoundingCard] = useState(false);

  useEffect(() => {
    getMyCard().then((c) => setHasFoundingCard(!!c)).catch(() => {});
  }, []);

  const [form, setForm] = useState({
    first_name: student?.first_name || '',
    last_name: student?.last_name || '',
    profile_name: student?.profile_name || '',
    school_name: student?.school_name || '',
    stream: (student?.stream || '') as Stream,
  });

  // Avatar upload state
  const fileRef = useRef<HTMLInputElement>(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  // Founding Student status (earned by contributing feedback) — persistent badge.
  const [isFounding, setIsFounding] = useState(false);
  useEffect(() => {
    feedbackAPI
      .getVolunteer()
      .then((v) => setIsFounding(!!v.founding_student))
      .catch(() => {/* badge is optional — never block the profile */});
  }, []);

  const resetForm = () => {
    setForm({
      first_name: student?.first_name || '',
      last_name: student?.last_name || '',
      profile_name: student?.profile_name || '',
      school_name: student?.school_name || '',
      stream: (student?.stream || '') as Stream,
    });
    setSaveError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await studentAPI.updateProfile({
        first_name: form.first_name.trim() || undefined,
        last_name: form.last_name.trim(),
        profile_name: form.profile_name.trim(),
        school_name: form.school_name.trim(),
        stream: form.stream || undefined,
      });
      await refreshProfile();
      setEditing(false);
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || 'Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarPick = () => fileRef.current?.click();

  const handleAvatarFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setAvatarError('Image too large (max 5 MB).');
      return;
    }
    setAvatarBusy(true);
    setAvatarError(null);
    try {
      await studentAPI.uploadAvatar(file);
      await refreshProfile();
    } catch (err: any) {
      setAvatarError(err?.response?.data?.detail || 'Upload failed. Try again.');
    } finally {
      setAvatarBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleAvatarRemove = async () => {
    if (!window.confirm('Remove your photo?')) return;
    setAvatarBusy(true);
    setAvatarError(null);
    try {
      await studentAPI.removeAvatar();
      await refreshProfile();
    } catch (err: any) {
      setAvatarError(err?.response?.data?.detail || 'Could not remove photo.');
    } finally {
      setAvatarBusy(false);
    }
  };

  const streamLabel = (s: string | undefined | null) => {
    if (s === 'natural') return 'Natural Science';
    if (s === 'social') return 'Social Science';
    return 'Not set';
  };

  const copyId = () => {
    if (!student?.student_id) return;
    navigator.clipboard?.writeText(student.student_id).catch(() => {});
  };

  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  const handleLinkTelegram = async () => {
    setLinking(true);
    setLinkError(null);
    try {
      const { deep_link_url } = await accountLinkAPI.requestToken();
      // Open the bot in a new tab. The bot's /start handler merges the
      // account; the user returns here and refreshes to see the link.
      window.open(deep_link_url, '_blank', 'noopener');
    } catch (err: any) {
      setLinkError(
        err?.response?.data?.detail || 'Could not start the link. Please try again.',
      );
    } finally {
      setLinking(false);
    }
  };

  return (
    <div className="profile-page">
      <header className="profile-header">
        <button onClick={() => navigate(-1)} className="back-button">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </button>
        <div className="header-brand" onClick={() => navigate('/dashboard')}>
          <Logo height={32} withAmharic />
        </div>
        <InboxBell />
      </header>

      <main className="profile-main">
        <div className="profile-content">
          {hasFoundingCard && (
            <button
              type="button"
              onClick={() => navigate('/first-students')}
              style={{
                width: '100%', textAlign: 'left', cursor: 'pointer', border: '1px solid #efd579',
                borderRadius: 16, padding: '14px 18px', marginBottom: 16, display: 'flex',
                alignItems: 'center', gap: 12,
                background: 'linear-gradient(135deg,#fff7d8,#ffe7a0)', color: '#8a6708',
              }}
            >
              <span style={{ fontSize: 22 }}>🍋</span>
              <span style={{ flex: 1 }}>
                <span style={{ display: 'block', fontWeight: 800 }}>Your Founding Class card</span>
                <span style={{ fontSize: 13, opacity: 0.85 }}>You were here at the beginning — see &amp; share it</span>
              </span>
              <span style={{ fontWeight: 800 }}>→</span>
            </button>
          )}
          {/* Identity */}
          <div className="avatar-section">
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <Avatar
                url={student?.avatar_url}
                name={student?.first_name}
                size={100}
              />
              <button
                type="button"
                onClick={handleAvatarPick}
                disabled={avatarBusy}
                aria-label="Change photo"
                title="Change photo"
                style={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: dark ? '#86c232' : '#4a7c10',
                  color: 'white',
                  border: '2px solid white',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ✎
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleAvatarFile}
                style={{ display: 'none' }}
              />
            </div>
            <h1 className="profile-name">
              {student?.display_name || student?.first_name || 'Student'}
            </h1>
            <p className="profile-subtitle">Student</p>
            {student?.avatar_url && student.avatar_source === 'upload' && (
              <button
                type="button"
                onClick={handleAvatarRemove}
                disabled={avatarBusy}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: dark ? '#7d8590' : '#94a3b8',
                  fontSize: 12,
                  cursor: 'pointer',
                  marginTop: 4,
                }}
              >
                Remove photo
              </button>
            )}
            {avatarError && (
              <p className="form-error" style={{ marginTop: 8 }}>
                {avatarError}
              </p>
            )}
          </div>

          {/* Your Profile — editable */}
          <div className="info-card">
            <div className="card-head">
              <h3 className="card-title">Your profile</h3>
              {!editing ? (
                <button className="link-btn" onClick={() => setEditing(true)}>Edit</button>
              ) : (
                <div className="edit-actions">
                  <button
                    className="link-btn ghost"
                    onClick={() => { resetForm(); setEditing(false); }}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    className="link-btn primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              )}
            </div>

            {!editing ? (
              <div className="info-list">
                <div className="info-item">
                  <span className="info-label">Profile name</span>
                  <span className="info-value">{student?.profile_name || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">First name</span>
                  <span className="info-value">{student?.first_name || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Last name</span>
                  <span className="info-value">{student?.last_name || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">School</span>
                  <span className="info-value">{student?.school_name || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Stream</span>
                  <span className="info-value">{streamLabel(student?.stream)}</span>
                </div>
              </div>
            ) : (
              <div className="edit-form">
                <label className="field">
                  <span className="field-label">Profile name</span>
                  <input
                    type="text"
                    value={form.profile_name}
                    onChange={e => setForm({ ...form, profile_name: e.target.value })}
                    maxLength={50}
                    placeholder="Shown on the leaderboard, channel, and shareable cards"
                    disabled={saving}
                  />
                  <small style={{ color: dark ? '#7d8590' : '#94a3b8', fontSize: 12 }}>
                    Leave blank to use your first name.
                  </small>
                </label>
                <label className="field">
                  <span className="field-label">First name</span>
                  <input
                    type="text"
                    value={form.first_name}
                    onChange={e => setForm({ ...form, first_name: e.target.value })}
                    maxLength={100}
                    disabled={saving}
                  />
                </label>
                <label className="field">
                  <span className="field-label">Last name</span>
                  <input
                    type="text"
                    value={form.last_name}
                    onChange={e => setForm({ ...form, last_name: e.target.value })}
                    maxLength={100}
                    disabled={saving}
                  />
                </label>
                <label className="field">
                  <span className="field-label">School</span>
                  <input
                    type="text"
                    value={form.school_name}
                    onChange={e => setForm({ ...form, school_name: e.target.value })}
                    maxLength={200}
                    disabled={saving}
                  />
                </label>
                <label className="field">
                  <span className="field-label">Stream</span>
                  <select
                    value={form.stream}
                    onChange={e => setForm({ ...form, stream: e.target.value as Stream })}
                    disabled={saving}
                  >
                    <option value="">Not set</option>
                    <option value="natural">Natural Science</option>
                    <option value="social">Social Science</option>
                  </select>
                </label>
                {saveError && <p className="form-error">{saveError}</p>}
              </div>
            )}
          </div>

          {/* Founding Student — earned by sharing feedback that shapes Lomi. */}
          {isFounding && (
            <div
              className="info-card"
              style={{
                background: dark
                  ? 'linear-gradient(135deg, rgba(245, 176, 65, 0.14) 0%, rgba(245, 176, 65, 0.05) 100%)'
                  : 'linear-gradient(135deg, #fff7e0 0%, #fffdf5 100%)',
                border: dark ? '1.5px solid rgba(245, 176, 65, 0.5)' : '1.5px solid #f5b301',
              }}
            >
              <h3 className="card-title" style={{ marginBottom: '0.35rem' }}>
                ⭐ Founding Student
              </h3>
              <p style={{ margin: 0, color: dark ? '#aab1ba' : '#475569', fontSize: '0.95rem' }}>
                You helped shape Lomi from the start — thank you. As a thank-you,
                you’ll keep Lomi Premium through the exam.
              </p>
            </div>
          )}

          {/* Share your Lomi story — always-on, pull (no outbound messages).
              A neutral/positive surface (profile), not a low moment, per
              feedback_no_identity_prompts_on_low_moments. Permanent entry
              (non-dismissible); the dashboard hosts the prominent dismissible
              hero variant. */}
          <StoryInviteCard
            source="web_profile"
            defaultName={student?.first_name || student?.display_name || ''}
          />

          {/* Read-only info */}
          <div className="info-card">
            <h3 className="card-title">Account</h3>
            <div className="info-list">
              <div className="info-item">
                <span className="info-label">Grade</span>
                <span className="info-value">Grade {student?.grade_level || '12'}</span>
              </div>
              {student?.phone_number && (
                <div className="info-item">
                  <span className="info-label">Phone</span>
                  <span className="info-value">{student.phone_number}</span>
                </div>
              )}
              {/* Student ID hidden 2026-05-03 — internal identifier with no
                  user-facing utility. Keep showId/copyId state in case we
                  bring it back via an admin-gated profile section. */}
            </div>
          </div>

          {/* Telegram link card — shown only when the student hasn't already
              claimed a Telegram identity. Clicking opens the bot in a new
              tab via a one-time link token; on return, a page refresh
              shows the Telegram row in the Account card above. */}
          {!student?.telegram_id && (
            <div className="info-card">
              <h3 className="card-title">📲 Link Telegram</h3>
              <p style={{ margin: '0 0 0.75rem', color: dark ? '#aab1ba' : '#475569', fontSize: '0.95rem' }}>
                Use the same account on our Telegram bot. Daily past-exam
                questions, leaderboard, and instant practice — from your
                phone. If you already have a Lomi bot account, we'll merge
                your progress.
              </p>
              <button
                className="link-btn primary"
                onClick={handleLinkTelegram}
                disabled={linking}
              >
                {linking ? 'Opening…' : 'Link my Telegram account'}
              </button>
              {linkError && (
                <p className="form-error" style={{ marginTop: '0.5rem' }}>
                  {linkError}
                </p>
              )}
            </div>
          )}

          {/* Follow our Telegram channel — visible to every student,
              even those already linked to the bot, because the channel
              (@lomistudy) is a separate subscription from the bot
              (@Lomistudybot). Daily past-exam questions + leaderboard
              in a scrollable feed. */}
          <div className="info-card">
            <h3 className="card-title">📢 Follow @lomistudy</h3>
            <p style={{ margin: '0 0 0.75rem', color: dark ? '#aab1ba' : '#475569', fontSize: '0.95rem' }}>
              Our public Telegram channel drops a real past-exam question
              every morning, weekly leaderboards, and throwback questions
              from older years. Free, no signup.
            </p>
            <a
              href="https://t.me/lomistudy"
              target="_blank"
              rel="noopener noreferrer"
              className="link-btn primary"
              style={{ textDecoration: 'none', display: 'inline-block' }}
            >
              Open @lomistudy
            </a>
          </div>

          {/* Join the public community group (@lomicommunity) — linked to the
              channel. Where students help shape Lomi, ask questions, and stick
              around between exams. Separate from the one-way channel feed. */}
          <div className="info-card">
            <h3 className="card-title">💬 Join the Lomi community</h3>
            <p style={{ margin: '0 0 0.75rem', color: dark ? '#aab1ba' : '#475569', fontSize: '0.95rem' }}>
              Our public group — ask questions, swap tips, help shape what Lomi
              builds next, and keep going with other students. Everyone’s welcome.
            </p>
            <a
              href="https://t.me/lomicommunity"
              target="_blank"
              rel="noopener noreferrer"
              className="link-btn primary"
              style={{ textDecoration: 'none', display: 'inline-block' }}
            >
              Open @lomicommunity
            </a>
          </div>

          {/* Lomi Live — kept as a quiet link here after the live event ended. */}
          <div className="info-card">
            <h3 className="card-title">🍋 Lomi Live</h3>
            <p style={{ margin: '0 0 0.75rem', color: dark ? '#aab1ba' : '#475569', fontSize: '0.95rem' }}>
              Mock-exam competitions and results. See past results or join the next one.
            </p>
            <button
              className="link-btn primary"
              style={{ textDecoration: 'none', display: 'inline-block' }}
              onClick={() => navigate('/live')}
            >
              Open Lomi Live
            </button>
          </div>

          {/* Actions */}
          <div className="profile-actions">
            <button className="action-btn secondary" onClick={() => navigate('/about')}>
              About Lomi
            </button>
            <button className="action-btn danger" onClick={logout}>
              Logout
            </button>
          </div>

          {/* Footer support-email removed 2026-05-03 per direction. */}
        </div>
      </main>
    </div>
  );
};

export default Profile;
